import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Typography, Select, Alert, Button, Space, Divider, Table, Tag, Input, DatePicker, Tooltip, message, Progress } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { BatchUpdateSlotItem, BatchUpdateSlotRequest, SlotStatus } from '../../../Modules/Slot/Model/Slot';
import { checkSlotsExists, batchUpdateSlots } from '../../../Modules/Slot/Presentation/actions';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { canEditSlotDates } from '../../../Utils/limitedPartnerAdmin';
import { dtoTriggersAsyncRank } from '../../../Utils/slotRankPolling';

const { Paragraph, Text } = Typography;
const { Option } = Select;

interface Props {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onSuccess: (rankPollIds?: number[]) => void;
}

type RawRow = Record<string, any>;

// 수정 가능한 필드 목록 (id 포함)
type Field = keyof BatchUpdateSlotItem | 'slotId';

const headerMap: Record<string, string[]> = {
  id: ['id', 'ID', 'slotId', 'slot_id', '슬롯ID', '슬롯번호', '번호', '슬롯 번호'],
  main_keyword: ['main_keyword', '메인키워드', '메인 키워드'],
  word_keyword: ['word_keyword', '작업키워드', '작업 키워드', '서브키워드', '서브 키워드'],
  mid_single: ['mid_single', '단일MID', '단일 MID'],
  mid_compare: ['mid_compare', '가격비교MID', '가격비교 MID', '원부MID', '원부 MID'],
  product_url: ['product_url', '상품URL', '상품 URL'],
  compare_url: ['compare_url', '가격비교URL', '가격비교 URL', '가격비교', '가격 비교', '가격비교링크'],
  memo: ['memo', '메모'],
  start_date: ['start_date', '시작일', 'start', 'Start'],
  end_date: ['end_date', '종료일', 'end', 'End'],
  status: ['status', '상태'],
  manager: ['manager', '매니저', '담당'],
  sellerId: ['아이디', '셀러', '판매자'],
};

const SELLER_HEADER = '아이디';

const ExcelBatchUpdateModal: React.FC<Props> = ({ open, file, onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);
  const canEditDates = canEditSlotDates(currentUser);

  const sellers = useMemo(
    () => accounts.filter(a => a.grade === 'SELLER').sort((a, b) => a.username.localeCompare(b.username)),
    [accounts]
  );

  const [rawRows, setRawRows] = useState<RawRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBatchUpdating, setIsBatchUpdating] = useState(false);
  const [batchUpdateTotal, setBatchUpdateTotal] = useState(0);
  const [batchUpdateCurrent, setBatchUpdateCurrent] = useState(0);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isBatchUpdating) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isBatchUpdating]);

  
  type RowDraft = Partial<BatchUpdateSlotItem> & { 
    key: number; 
    exists?: boolean; 
    errorMsg?: string;
    originalId?: number; // 엑셀에서 처음 읽어온 ID
  };
  
  const [rows, setRows] = useState<RowDraft[]>([]);
  const [bulkSellerId, setBulkSellerId] = useState<number | undefined>(undefined);
  
  const [mapping, setMapping] = useState<Record<string, string | null>>({
    id: null,
    main_keyword: null,
    word_keyword: null,
    mid_single: null,
    mid_compare: null,
    product_url: null,
    compare_url: null,
    memo: null,
    start_date: null,
    end_date: null,
    status: null,
    manager: null,
  });

  // 반응형: 모바일 여부
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open) {
      setRawRows([]);
      setRows([]);
      setIsValidated(false);
      setIsChecking(false);
      setIsUpdating(false);
      setIsParsing(false);
      return;
    }
    if (!file) return;
    setParseError(null);
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<RawRow>(ws, { defval: '' });
        setRawRows(rows);
      } catch (err) {
        setParseError('엑셀 파일을 읽는 중 오류가 발생했습니다.');
      } finally {
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      setParseError('엑셀 파일을 읽을 수 없습니다.');
      setIsParsing(false);
    };
    reader.readAsArrayBuffer(file);
  }, [open, file]);

  const detectedHeaders = useMemo(() => {
    const set = new Set<string>();
    rawRows.forEach(r => Object.keys(r).forEach(k => set.add(String(k))));
    return Array.from(set);
  }, [rawRows]);

  const resolveSellerId = useCallback((value: any): number | undefined => {
    if (value == null || value === '') return undefined;
    
    const raw = String(value).trim();
    if (!raw) return undefined;

    // 1) 정확 일치 (username) - 최우선 (숫자로만 이루어진 경우 포함)
    const usernameCandidate = raw.split('(')[0].trim().split(/\s+/)[0].trim();
    const lower = usernameCandidate.toLowerCase();
    const matched = sellers.find(s => s.username.toLowerCase() === lower);
    if (matched) return matched.id;

    // 2) 숫자인 경우 ID로 검색
    if (/^\d+$/.test(raw)) return Number(raw);

    return undefined;
  }, [sellers]);

  useEffect(() => {
    if (detectedHeaders.length === 0) return;
    const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
    const next: Record<string, string | null> = { ...mapping };
    let changed = false;
    (Object.keys(headerMap)).forEach(field => {
      if (next[field]) return;
      const aliases = headerMap[field].map(norm);
      const found = detectedHeaders.find(h => aliases.includes(norm(h)) || aliases.some(a => norm(h).includes(a)));
      if (found) {
        next[field] = found;
        changed = true;
      }
    });
    if (changed) setMapping(next);
  }, [detectedHeaders, mapping]);

  const toDateString = (value: any): string => {
    if (value == null || value === '') return '';
    if (typeof value === 'number') {
      const date = new Date(Math.round((value - 25569) * 86400 * 1000));
      return dayjs(date).format('YYYY-MM-DD');
    }
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : String(value);
  };

  useEffect(() => {
    const norm = (s: any) => String(s ?? '').trim();
    const initial = rawRows.map((r, idx) => {
      const pick = (field: string): any => {
        const header = mapping[field];
        if (!header) return '';
        return r[header];
      };
      const idRaw = pick('id');
      const sellerRaw = r[mapping['sellerId'] || SELLER_HEADER] ?? r['아이디'] ?? r['셀러(id)'] ?? r['셀러'];
      const startRaw = pick('start_date');
      const endRaw = pick('end_date');
      
      const parsedId = Number(norm(idRaw));

      return {
        key: idx,
        id: isNaN(parsedId) ? undefined : parsedId,
        originalId: isNaN(parsedId) ? undefined : parsedId,
        sellerId: resolveSellerId(sellerRaw),
        manager: (pick('manager') as any) ?? null,
        main_keyword: norm(pick('main_keyword')),
        word_keyword: "", // 작업 키워드는 빈 값으로 고정
        mid_single: norm(pick('mid_single')),
        mid_compare: norm(pick('mid_compare')),
        product_url: norm(pick('product_url')),
        compare_url: norm(pick('compare_url')),
        memo: norm(pick('memo')) || undefined,
        start_date: canEditDates ? toDateString(startRaw) : undefined, // 어드민만 엑셀 날짜 읽어옴
        end_date: canEditDates ? toDateString(endRaw) : undefined,   // 어드민만 엑셀 날짜 읽어옴
        status: (norm(pick('status')) as SlotStatus) || '정상',
      } as RowDraft;
    });
    setRows(initial);
    setIsValidated(false); // 새로운 데이터 로드 시 검증 초기화
    
    // 파일 로드 직후 자동으로 첫 검증 실행
    if (initial.length > 0) {
      const idsToCheck = initial
        .map(r => r.id)
        .filter((id): id is number => typeof id === 'number' && id > 0);
      
      if (idsToCheck.length > 0) {
        const autoValidate = async () => {
          setIsChecking(true);
          try {
            const result = await dispatch(checkSlotsExists({ request: { slot_ids: idsToCheck } })).unwrap();
            const existingSet = new Set(result.existing_ids);
            
            setRows(prev => prev.map(r => {
              const currentId = r.id;
              if (typeof currentId !== 'number') return { ...r, exists: false, errorMsg: 'ID 없음' };
              const exists = existingSet.has(currentId);
              return { ...r, exists, errorMsg: exists ? undefined : '존재하지 않거나 권한 없음' };
            }));
            setIsValidated(true);
          } catch (err) {
            console.error('Initial check exists error:', err);
          } finally {
            setIsChecking(false);
          }
        };
        autoValidate();
      }
    }
  }, [rawRows, mapping, resolveSellerId, dispatch]);

  // 슬롯 존재 여부 체크 함수 (수동 실행)
  const handleValidate = async () => {
    if (rows.length === 0 || isChecking) return;
    
    const idsToCheck = rows
      .map(r => r.id)
      .filter((id): id is number => typeof id === 'number' && id > 0);
    
    if (idsToCheck.length === 0) {
      message.warning('검증할 슬롯 ID가 없습니다.');
      return;
    }

    setIsChecking(true);
    try {
      const result = await dispatch(checkSlotsExists({ request: { slot_ids: idsToCheck } })).unwrap();
      const existingSet = new Set(result.existing_ids);
      
      setRows(prev => prev.map(r => {
        if (typeof r.id !== 'number') return { ...r, exists: false, errorMsg: 'ID 없음' };
        const exists = existingSet.has(r.id);
        return { ...r, exists, errorMsg: exists ? undefined : '존재하지 않거나 권한 없음' };
      }));
      setIsValidated(true);
      message.success('검증이 완료되었습니다.');
    } catch (err) {
      console.error('Check exists error:', err);
      message.error('슬롯 검증 중 오류가 발생했습니다.');
    } finally {
      setIsChecking(false);
    }
  };

  const getRowErrors = useCallback((r: RowDraft): string[] => {
    const errs: string[] = [];
    if (!r.id) errs.push('ID 없음');
    else if (!r.exists) errs.push(r.errorMsg || '슬롯 확인 불가');
    
    if (!r.sellerId) errs.push('셀러 미선택');
    
    if (canEditDates) {
        if (!r.start_date) errs.push('시작일 없음');
        if (!r.end_date) errs.push('종료일 없음');
        if (r.start_date && r.end_date) {
            const s = dayjs(r.start_date).startOf('day');
            const e = dayjs(r.end_date).startOf('day');
            if (e.isBefore(s)) errs.push('종료일은 시작일 이후여야 합니다');
        }
    }
    
    if (r.product_url && !isValidUrl(r.product_url)) errs.push('상품 URL 형식이 올바르지 않습니다');
    if (r.compare_url && !isValidUrl(r.compare_url)) errs.push('가격비교 URL 형식이 올바르지 않습니다');
    
    return errs;
  }, [canEditDates]);

  const isRowValid = useCallback((r: RowDraft) => getRowErrors(r).length === 0, [getRowErrors]);

  const isValidUrl = (url: string) => {
    if (!url) return true;
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  };

  const { validRows, invalidCount } = useMemo(() => {
    const valid: RowDraft[] = [];
    rows.forEach(r => {
      if (isRowValid(r)) valid.push(r);
    });
    return { validRows: valid, invalidCount: rows.length - valid.length };
  }, [rows, isRowValid]);

  const canSubmit = useMemo(() => isValidated && rows.length > 0 && rows.every(isRowValid), [isValidated, rows, isRowValid]);

  const handleUpdate = async () => {
    if (!canSubmit) return;
    
    setIsUpdating(true);
    const updateItems: BatchUpdateSlotItem[] = rows.map(r => ({
      id: r.id!,
      sellerId: r.sellerId,
      manager: r.manager,
      main_keyword: r.main_keyword,
      word_keyword: r.word_keyword,
      mid_single: r.mid_single,
      mid_compare: r.mid_compare,
      product_url: r.product_url,
      compare_url: r.compare_url,
      memo: r.memo,
      start_date: canEditDates ? r.start_date : undefined,
      end_date: canEditDates ? r.end_date : undefined,
    }));

    const rankSensitiveIdSet = new Set(
      updateItems
        .filter((item) => dtoTriggersAsyncRank(item))
        .map((item) => item.id),
    );

    const totalCount = updateItems.length;
    const CHUNK_SIZE = 25;
    const THRESHOLD = 50;
    const DELAY_MS = 10000;

    if (totalCount <= THRESHOLD) {
      try {
        const result = await dispatch(batchUpdateSlots({ 
          request: { slots: updateItems },
          me: currentUser!
        })).unwrap();

        if (result.errors && result.errors.length > 0) {
          message.warning(`${result.updated_count}개 성공, ${result.errors.length}개 실패`);
        } else {
          message.success(`${result.updated_count}개의 슬롯이 수정되었습니다.`);
        }
        const rankPollIds = (result.updated_ids ?? []).filter((id) => rankSensitiveIdSet.has(id));
        onSuccess(rankPollIds);
      } catch (err: any) {
        message.error(err.message || '일괄 수정에 실패했습니다.');
      } finally {
        setIsUpdating(false);
      }
      return;
    }

    setIsBatchUpdating(true);
    setBatchUpdateTotal(totalCount);
    setBatchUpdateCurrent(0);

    let successCount = 0;
    let failCount = 0;
    const rankPollIds: number[] = [];

    const chunks: BatchUpdateSlotItem[][] = [];
    for (let i = 0; i < updateItems.length; i += CHUNK_SIZE) {
      chunks.push(updateItems.slice(i, i + CHUNK_SIZE));
    }

    try {
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        try {
          const result = await dispatch(batchUpdateSlots({ 
            request: { slots: chunk },
            me: currentUser!
          })).unwrap();
          
          successCount += result.updated_count;
          failCount += (chunk.length - result.updated_count);
          (result.updated_ids ?? []).forEach((id) => {
            if (rankSensitiveIdSet.has(id)) rankPollIds.push(id);
          });
          setBatchUpdateCurrent(successCount + failCount);
          
          if (i < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, DELAY_MS));
          }
        } catch (err) {
          failCount += chunk.length;
          setBatchUpdateCurrent(successCount + failCount);
        }
      }
      if (failCount === 0) {
        message.success(`총 ${successCount}개 수정 완료`);
      } else {
        message.warning(`수정 완료: 성공 ${successCount}개, 실패 ${failCount}개`);
      }
      onSuccess(rankPollIds);
    } finally {
      setIsUpdating(false);
      setIsBatchUpdating(false);
    }
  };

  const applyBulkSeller = useCallback((mode: 'all' | 'emptyOnly') => {
    if (!bulkSellerId) return;
    setRows(prev =>
      prev.map(r => {
        if (mode === 'emptyOnly' && r.sellerId) return r;
        return { ...r, sellerId: bulkSellerId };
      })
    );
  }, [bulkSellerId]);

  const Metrics = styled(Space)`
    width: 100%;
  `;

  const FullWidth = styled(Space)`
    width: 100%;
  `;

  const StyledTable = styled(Table)`
    .row-invalid {
      background-color: #fff1f0;
      &:hover > td {
        background-color: #fff1f0 !important;
      }
    }
  ` as typeof Table;

  return (
    <Modal
      title="엑셀 일괄 수정 - 행별 편집 및 확인"
      open={open}
      onCancel={onClose}
      width={isMobile ? undefined : 1200}
      bodyStyle={isMobile ? { paddingBottom: 0 } : { paddingBottom: 0, maxHeight: '80vh', overflowY: 'auto' }}
      closable={!(isUpdating || isBatchUpdating)}
      maskClosable={!(isUpdating || isBatchUpdating)}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isUpdating || isBatchUpdating}>취소</Button>,
        <Button 
            key="submit" 
            type="primary" 
            disabled={!canSubmit || isBatchUpdating} 
            loading={isUpdating}
            onClick={handleUpdate}
          >
            수정 반영
          </Button>,
      ]}
    >
      <FullWidth direction="vertical" size="middle">
        {isBatchUpdating && (
          <Alert
            message="분할 수정 진행 중"
            description={
              <div style={{ marginTop: 8 }}>
                <Text>전체 {batchUpdateTotal}개 중 {batchUpdateCurrent}개 완료</Text>
                <Progress 
                  percent={Math.floor((batchUpdateCurrent / batchUpdateTotal) * 100)} 
                  status="active" 
                  strokeColor="#B03052"
                />
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                  * 브라우저 창을 닫거나 새로고침하면 수정이 중단될 수 있습니다.
                </Text>
              </div>
            }
            type="info"
            showIcon
          />
        )}
        <Paragraph>
          엑셀 내용을 분석했습니다. <strong>ID(슬롯번호)</strong>와 <strong>셀러</strong>를 확인해 주세요.<br/>
          {canEditDates
            ? '일반 관리자 권한으로 시작일/종료일 수정이 가능합니다.'
            : '제한형 관리자는 시작일/종료일을 수정할 수 없습니다.'}
        </Paragraph>

        {parseError && <Alert type="error" message={parseError} />}

        <Metrics size="small">
          <Space size="small"><Text>총 행 수:</Text><Text strong>{rows.length}</Text></Space>
          <Space size="small"><Text>검증된 수정 가능:</Text><Text strong>{isValidated ? validRows.length : '-'}</Text></Space>
          {isValidated && invalidCount > 0 && (
            <Space size="small"><Text>오류 행:</Text><Text type="danger">{invalidCount}</Text></Space>
          )}
          {isChecking && <Text type="secondary">슬롯 존재 여부 확인 중...</Text>}
          {!isValidated && !isChecking && <Tag color="warning">검증 필요</Tag>}
          {isValidated && invalidCount === 0 && <Tag color="success">검증 완료</Tag>}
        </Metrics>

        <Divider style={{ margin: '8px 0' }} />

        <Space wrap size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap size="small">
            <Text strong>셀러 일괄 적용</Text>
            <Select
              value={bulkSellerId}
              style={{ width: 200 }}
              placeholder="셀러 선택"
              showSearch
              optionFilterProp="label"
              optionLabelProp="label"
              onChange={(val: number) => setBulkSellerId(val)}
              allowClear
            >
              {sellers.map(s => (
                <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                  {s.username} ({s.name})
                </Option>
              ))}
            </Select>
            <Button
              onClick={() => applyBulkSeller('all')}
              disabled={!bulkSellerId || rows.length === 0}
            >
              전체 적용
            </Button>
            <Button 
              type="primary" 
              danger 
              icon={<SearchOutlined />} 
              onClick={handleValidate} 
              loading={isChecking}
              disabled={rows.length === 0}
            >
              슬롯 존재 및 권한 검증
            </Button>
          </Space>
          <Text type="secondary">오류가 있는 행(붉은색)은 수정이 불가능합니다. ID나 셀러를 확인하세요.</Text>
        </Space>

        <StyledTable
          size="small"
          pagination={false}
          dataSource={rows}
          scroll={{ x: 2000 }}
          loading={isChecking || isParsing}
          columns={[
            {
              title: '슬롯 ID',
              dataIndex: 'id',
              width: 120,
              fixed: 'left',
              render: (v: any, r: RowDraft) => {
                const isError = !r.exists;
                return (
                  <Tooltip title={r.errorMsg}>
                    <Input 
                      value={v} 
                      status={isError ? 'error' : undefined}
                      disabled={r.exists} // 오류가 아닐 때만 수정 불가
                      onChange={async e => {
                        const val = e.target.value;
                        if (val === '') {
                          setRows(prev => prev.map(x => x.key === r.key ? { ...x, id: undefined, exists: false, errorMsg: 'ID 없음' } : x));
                          return;
                        }
                        const newId = Number(val);
                        if (isNaN(newId)) return;
                        
                        // ID 변경 시 다시 체크가 필요하므로 상태 업데이트
                        const updatedRow = { ...r, id: newId, exists: undefined, errorMsg: undefined };
                        setRows(prev => prev.map(x => x.key === r.key ? updatedRow : x));
                        setIsValidated(false);
                      }}
                    />
                  </Tooltip>
                );
              }
            },
            {
              title: '셀러',
              dataIndex: 'sellerId',
              width: 180,
              render: (_: unknown, r: RowDraft) => (
                <Select
                  value={r.sellerId}
                  style={{ width: '100%' }}
                  placeholder="셀러 선택"
                  showSearch
                  optionFilterProp="label"
                  optionLabelProp="label"
                  status={!r.sellerId ? 'error' : undefined}
                  onChange={(val: number) => setRows(rows => rows.map(x => x.key === r.key ? { ...x, sellerId: val } : x))}
                >
                  {sellers.map(s => (
                    <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                      {s.username} ({s.name})
                    </Option>
                  ))}
                </Select>
              )
            },
            { title: '메인키워드', dataIndex: 'main_keyword', width: 150, render: (v: any, r: any) => (
              <Input value={v} onChange={e => setRows(rows => rows.map(x => x.key === r.key ? { ...x, main_keyword: e.target.value } : x))} />
            ) },
            { title: '단일MID', dataIndex: 'mid_single', width: 150, render: (v: any, r: any) => (
              <Input value={v} onChange={e => setRows(rows => rows.map(x => x.key === r.key ? { ...x, mid_single: e.target.value } : x))} />
            ) },
            { title: '비교MID', dataIndex: 'mid_compare', width: 150, render: (v: any, r: any) => (
              <Input value={v} onChange={e => setRows(rows => rows.map(x => x.key === r.key ? { ...x, mid_compare: e.target.value } : x))} />
            ) },
            { title: '상품URL', dataIndex: 'product_url', width: 200, render: (v: string, r: RowDraft) => (
              <Tooltip title={v && !isValidUrl(v) ? '올바른 URL 형식이 아닙니다 (http:// 또는 https:// 포함)' : ''}>
                <Input 
                  value={v} 
                  status={v && !isValidUrl(v) ? 'error' : undefined}
                  onChange={e => setRows(rows => rows.map(x => x.key === r.key ? { ...x, product_url: e.target.value } : x))} 
                />
              </Tooltip>
            ) },
            { title: '비교URL', dataIndex: 'compare_url', width: 200, render: (v: string, r: RowDraft) => (
              <Tooltip title={v && !isValidUrl(v) ? '올바른 URL 형식이 아닙니다 (http:// 또는 https:// 포함)' : ''}>
                <Input 
                  value={v} 
                  status={v && !isValidUrl(v) ? 'error' : undefined}
                  onChange={e => setRows(rows => rows.map(x => x.key === r.key ? { ...x, compare_url: e.target.value } : x))} 
                />
              </Tooltip>
            ) },
            { title: '시작일', dataIndex: 'start_date', width: 130, render: (v: string | undefined, r: RowDraft) => (
              <DatePicker
                value={v ? dayjs(v) : undefined}
                disabled={!canEditDates}
                onChange={d => setRows(rows => rows.map(x => x.key === r.key ? { ...x, start_date: d ? d.format('YYYY-MM-DD') : '' } : x))}
              />
            ) },
            { title: '종료일', dataIndex: 'end_date', width: 130, render: (v: string | undefined, r: RowDraft) => (
              <DatePicker
                value={v ? dayjs(v) : undefined}
                disabled={!canEditDates}
                onChange={d => setRows(rows => rows.map(x => x.key === r.key ? { ...x, end_date: d ? d.format('YYYY-MM-DD') : '' } : x))}
              />
            ) },
            { title: '메모', dataIndex: 'memo', width: 150, render: (v: any, r: any) => (
              <Input value={v} onChange={e => setRows(rows => rows.map(x => x.key === r.key ? { ...x, memo: e.target.value } : x))} />
            ) },
          ]}
          rowClassName={(r: RowDraft) => {
            if (!isValidated) return '';
            return isRowValid(r) ? '' : 'row-invalid';
          }}
        />
      </FullWidth>
    </Modal>
  );
};

export default ExcelBatchUpdateModal;
